// Dashboard widget registry (#253).
//
// A feature module that owns a widget shown on the Dashboard registers it here
// (`Report/dashboard-widgets.ts` is the model); the Dashboard renders it by id
// with <DashboardWidget id="report.target-revenue" />. Neither side imports the
// other: this file is neutral (src/lib may not import a module, see the
// `no-shared-to-module` lint rule), and both depend on it.
//
// Registration is lazy by construction: `load` is a dynamic import and the
// registry wraps it in React.lazy itself, so an owner cannot register a widget
// eagerly by mistake and the owner's code and locales stay out of the entry
// chunk. Registering costs one Map entry; call it at module scope from the
// owner's `index.tsx` (the way `./locales/register` is imported).
//
// Where a widget sits (department, order, column span) is Dashboard layout and
// stays in the Dashboard's department files: a layout mixes widgets of several
// owners (Executive shows Sales, Account and Finance widgets), so ownership
// and placement are separate questions.
import {
    lazy,
    Suspense,
    type ComponentProps,
    type ComponentType,
    type LazyExoticComponent,
} from 'react'
import { Card, CardBody, Skeleton } from '@vnetwork/datacentral-ui/primitives'

// Each widget takes its own props; the slot passes them through untyped.
type AnyComponent = ComponentType<any>

interface DashboardWidgetRegistration {
    /** `<module>.<widget>` in kebab-case, e.g. `report.target-revenue`. */
    id: string
    /** Dynamic import of the widget component: `() => import('./components/X')`. */
    load: () => Promise<{ default: AnyComponent }>
}

const registry = new Map<string, LazyExoticComponent<AnyComponent>>()

/** Register a widget under `id`. Registering the same id again replaces it. */
export const registerDashboardWidget = ({
    id,
    load,
}: DashboardWidgetRegistration): void => {
    registry.set(id, lazy(load))
}

const WidgetFallback = () => (
    <Card className="h-full">
        <CardBody>
            <Skeleton className="h-[200px] w-full" />
        </CardBody>
    </Card>
)

interface DashboardWidgetProps {
    id: string
    /** Props passed to the registered component. */
    props?: ComponentProps<AnyComponent>
}

/** Render the widget registered under `id` (nothing if none is). */
export const DashboardWidget = ({ id, props }: DashboardWidgetProps) => {
    const Widget = registry.get(id)
    if (!Widget) {
        if (import.meta.env.DEV)
            console.warn(`Dashboard widget "${id}" is not registered`)
        return null
    }
    return (
        <Suspense fallback={<WidgetFallback />}>
            <Widget {...props} />
        </Suspense>
    )
}
